import React, { memo } from 'react';
import { LABEL_TYPES } from '../utils/labelConstants';
import { tsToDateStr } from '../utils/labelUtils';
import { GeoIcon, PencilIcon, TrashIcon } from '../utils/LabelIcons';
import useLabelSmartPosition from '../hooks/useLabelSmartPosition';

// ORIGINE DES DONNÉES (labelData)
// ─────────────────────────────────────────────────────────────────────────────
// 1. Au chargement du graphe, LabelEventGraphGeoDemo appelle fetchGraphData()
//    (labelEventGraphApi.js). La réponse contient un tableau `labels` (format
//    API snake_case) converti en camelCase par mapApiLabel() puis stocké dans
//    l'état `labels` du conteneur.
//
// 2. LabelEventGraph reçoit ce tableau via la prop configuration.labels.
//    Il génère des marqueurs Plotly invisibles (labelHoverTraces) positionnés
//    sur chaque label ; chaque marqueur embarque le timestamp `t` dans son
//    champ `customdata`.
//
// 3. Quand l'utilisateur survole un de ces marqueurs, le callback handleHover
//    de LabelEventGraph détecte que customdata est un timestamp (≠ object
//    phase) et appelle onLabelHover(pt.customdata, clientX, clientY).
//
// 4. Dans le conteneur, handleLabelHover recherche dans `labels` l'objet dont
//    `l.t === t`, puis appelle setHoveredLabel({ label: found, x, y }).
//
// 5. Le conteneur affiche <LabelHoverCard labelData={hoveredLabel.label} …/>
//    → c'est l'objet { id, t, labelType, iconType, label, note } issu de
//    l'état local, lui-même synchronisé avec l'API.

const LabelHoverCard = memo(
  ({ labelData, x, y, onEdit, onRemove, onMouseEnter, onMouseLeave }) => {
    const labelConfig = LABEL_TYPES[labelData.labelType] || LABEL_TYPES.IA;
    const { ref, pos } = useLabelSmartPosition(x, y);

    return (
      <div
        ref={ref}
        className="leg-hover-anchor"
        style={{ left: pos.left, top: pos.top, visibility: pos.visibility }}
        onMouseEnter={onMouseEnter}
        onMouseLeave={onMouseLeave}
      >
        <div
          className="leg-hover-card"
          style={{ borderColor: labelConfig.color }}
        >
          <div className="leg-card-header">
            <div className="leg-card-type">
              <GeoIcon iconType={labelData.iconType} size={13} />
              <span
                className="leg-type-badge"
                style={{ background: labelConfig.color }}
              >
                <span className="leg-type-badge-inner">
                  {labelData.label || labelConfig.label}
                </span>
              </span>
            </div>
            <div className="leg-card-btn-group">
              <button className="leg-btn-edit" onClick={onEdit} title="Éditer">
                <PencilIcon size={14} />
              </button>
              <button
                className="leg-btn-trash"
                onClick={onRemove}
                title="Supprimer"
              >
                <TrashIcon size={14} />
              </button>
            </div>
          </div>
          <div
            className="leg-divider"
            style={{ borderColor: labelConfig.color + '44' }}
          />
          <div className="leg-card-row">
            <span className="leg-card-meta">Date</span>
            <span className="leg-card-value">{tsToDateStr(labelData.t)}</span>
          </div>

          {labelData.note && (
            <div className="leg-card-row">
              <span className="leg-card-meta">Note</span>
              <span className="leg-card-value leg-card-value--italic">
                {labelData.note}
              </span>
            </div>
          )}
          {labelData.labelType === 'IA' && labelData.precision != null && (
            <div className="leg-card-row">
              <span className="leg-card-meta">Précision</span>
              <span className="leg-card-value">{labelData.precision} %</span>
            </div>
          )}
          {labelData.labelType === 'IA' && labelData.urlSon && (
            <div className="leg-card-row">
              <span className="leg-card-meta">Son</span>
              <a
                href={
                  /^https?:\/\//i.test(labelData.urlSon)
                    ? labelData.urlSon
                    : `https://argos-v25.itmsol.fr/${labelData.urlSon}`
                }
                target="_blank"
                rel="noopener noreferrer"
                className="leg-sound-link"
                onClick={(e) => e.stopPropagation()}
              >
                {labelData.urlSon.split('/').pop()}
              </a>
            </div>
          )}
        </div>
      </div>
    );
  }
);

export default LabelHoverCard;
